import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import Icon from '../../../components/AppIcon';
import { purchaseAnalyticsService } from '../../../services/purchaseCenterService';

const SpendingInsights = ({ organizationId: propOrganizationId }) => {
  const { user } = useAuth();
  const [insights, setInsights] = useState(null);
  const [trends, setTrends] = useState([]);
  const [topVendors, setTopVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use prop organizationId or fallback to user context
  const organizationId = propOrganizationId || user?.user_metadata?.organization_id || user?.organization_id;

  useEffect(() => {
    if (organizationId) {
      fetchInsights();
    }
  }, [organizationId]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all analytics data
      const [metricsResult, trendsResult, vendorsResult] = await Promise.all([
        purchaseAnalyticsService?.getSpendingMetrics(organizationId),
        purchaseAnalyticsService?.getSpendingTrends(organizationId),
        purchaseAnalyticsService?.getTopVendors(organizationId, 5)
      ]);

      if (metricsResult?.error) throw metricsResult?.error;
      if (trendsResult?.error) throw trendsResult?.error;
      if (vendorsResult?.error) throw vendorsResult?.error;

      setInsights(metricsResult?.data);
      setTrends(trendsResult?.data || []);
      setTopVendors(vendorsResult?.data || []);
      
    } catch (error) {
      console.error('Error fetching spending insights:', error);
      setError(error?.message || 'Failed to load spending insights');
    } finally {
      setLoading(false);
    }
  };

  if (!organizationId) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Access Required</h3>
          <p className="text-muted-foreground text-sm">
            Please log in to view spending insights
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="Loader2" size={48} className="text-muted-foreground/30 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-foreground mb-2">Loading...</h3>
          <p className="text-muted-foreground text-sm">
            Fetching spending insights
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="AlertTriangle" size={48} className="text-red-500/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error</h3>
          <p className="text-muted-foreground text-sm mb-4">{error}</p>
          <button
            onClick={fetchInsights}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="mr-2 inline" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="BarChart3" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Data Available</h3>
          <p className="text-muted-foreground text-sm">
            Start adding vendors and bills to see spending insights
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Spending Insights Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">Spending Insights</h3>
          <button
            onClick={fetchInsights}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            title="Refresh insights"
          >
            <Icon name="RefreshCw" size={16} />
          </button>
        </div>

        {/* Key Metrics */}
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Icon name="DollarSign" size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Monthly Spending</p>
                <p className="text-xl font-semibold text-foreground">
                  ${insights?.totalMonthlySpending?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Icon name="Clock" size={20} className="text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Bills</p>
                <p className="text-xl font-semibold text-foreground">
                  ${insights?.totalPendingAmount?.toLocaleString() || '0'}
                </p>
              </div>
            </div>
          </div>

          {insights?.overdueCount > 0 && (
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Icon name="AlertCircle" size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-red-600">Overdue Bills</p>
                  <p className="text-xl font-semibold text-red-800">
                    {insights?.overdueCount} bills (${insights?.overdueAmount?.toLocaleString() || '0'})
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Icon name="Users" size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Vendors</p>
                <p className="text-xl font-semibold text-foreground">
                  {insights?.activeVendorCount || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Icon name="Timer" size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Payment Time</p>
                <p className="text-xl font-semibold text-foreground">
                  {insights?.avgPaymentTime || 0} days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Spending Trends */}
      {trends?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="text-md font-semibold text-foreground mb-4">Spending Trends (6 Months)</h4>
          <div className="space-y-3">
            {trends?.map((trend, index) => {
              const maxAmount = Math.max(...trends?.map(t => t?.amount));
              const percentage = maxAmount > 0 ? (trend?.amount / maxAmount) * 100 : 0;
              
              return (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground w-20">{trend?.month}</span>
                  <div className="flex-1 mx-3">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-medium text-foreground w-24 text-right">
                    ${trend?.amount?.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Vendors */}
      {topVendors?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          <h4 className="text-md font-semibold text-foreground mb-4">Top Vendors by Spending</h4>
          <div className="space-y-3">
            {topVendors?.map((vendor, index) => {
              const maxSpent = Math.max(...topVendors?.map(v => v?.totalSpent));
              const percentage = maxSpent > 0 ? (vendor?.totalSpent / maxSpent) * 100 : 0;
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {vendor?.name}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      ${vendor?.totalSpent?.toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-semibold text-foreground mb-4">Quick Actions</h4>
        <div className="space-y-2">
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors">
            <Icon name="Plus" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Add New Vendor</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors">
            <Icon name="FileText" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Create Bill</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors">
            <Icon name="CreditCard" size={16} className="text-primary" />
            <span className="text-sm text-foreground">Process Payment</span>
          </button>
          <button className="w-full flex items-center space-x-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors">
            <Icon name="BarChart3" size={16} className="text-primary" />
            <span className="text-sm text-foreground">View Full Report</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpendingInsights;